import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { InitialsAvatar } from '../../ui/initials-avatar';
import type { NominableClientContact } from '../../../hooks/useNominableClientContacts';
import { cn } from '../../../lib/utils';

interface ClientContactPickerModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Already filtered: no guests, no cancelled contacts. */
	contacts: NominableClientContact[];
	loadingContacts?: boolean;
	selectedIds: string[];
	onSave: (ids: string[]) => void;
	saving?: boolean;
	/** The backend's reason per refused id, from the last save. */
	refused?: Record<string, string>;
}

/**
 * Picks who at the client hears about a quote. Unlike the admin picker the
 * contacts are passed in — the section already loads them to show names, and a
 * client organisation is small enough to filter locally.
 */
export function ClientContactPickerModal({
	open,
	onOpenChange,
	contacts,
	loadingContacts,
	selectedIds,
	onSave,
	saving,
	refused = {},
}: ClientContactPickerModalProps) {
	const [draft, setDraft] = useState<string[]>([]);
	const [query, setQuery] = useState('');

	useEffect(() => {
		if (open) {
			setDraft(selectedIds);
			setQuery('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed the draft only when the modal opens, not on selectedIds changes mid-edit
	}, [open]);

	const toggle = (id: string) => {
		setDraft((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
	};

	const q = query.trim().toLowerCase();
	const filtered = q
		? contacts.filter((c) => `${c.name} ${c.email ?? ''}`.toLowerCase().includes(q))
		: contacts;

	// Ids on the quote the list can no longer show (left, cancelled, became a
	// guest — the list doesn't say which). Kept in the draft so a save doesn't
	// silently drop them — the backend's refusal then names them. Not judged
	// until the list has loaded: before that every id looks missing, and
	// "Remove" would wipe the whole selection.
	const hidden = loadingContacts ? [] : draft.filter((id) => !contacts.some((c) => c.id === id));

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Client contacts</DialogTitle>
					<DialogDescription>
						Who at the client gets emails about this quote: offers, deadlines, orders,
						samples and payments. Choose nobody to email everyone. Guest-link holders
						are always emailed.
					</DialogDescription>
				</DialogHeader>

				<DialogBody className="flex flex-col gap-0 p-0">
					<div className="relative shrink-0 px-4 pt-4 pb-2">
						<Search
							size={14}
							className="pointer-events-none absolute left-7 top-1/2 -translate-y-1/2 text-text-4"
						/>
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search contacts…"
							className="h-8 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-9 pr-3 text-base text-text-1 outline-none placeholder:text-text-4 focus:border-line-3"
							autoFocus
						/>
					</div>

					<div className="scroll-thin h-[260px] overflow-y-auto px-4 py-1">
						<div className="flex flex-col gap-0.5">
							{filtered.map((contact) => {
								const isSelected = draft.includes(contact.id);
								const reason = refused[contact.id];
								return (
									<button
										key={contact.id}
										onClick={() => toggle(contact.id)}
										className={cn(
											'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-surface-hover',
											isSelected && 'bg-surface-fill'
										)}
									>
										<Checkbox
											checked={isSelected}
											tabIndex={-1}
											className="pointer-events-none"
										/>
										<InitialsAvatar name={contact.name} size={28} />
										<div className="min-w-0 flex-1">
											<div className="truncate text-base font-medium text-text-1">
												{contact.name}
											</div>
											{reason ? (
												<div className="text-sm text-status-danger">
													{reason}
												</div>
											) : (
												contact.email && (
													<div className="truncate text-sm text-text-4">
														{contact.email}
													</div>
												)
											)}
										</div>
										{contact.pending && (
											<span className="shrink-0 text-xs text-text-4">
												Invited
											</span>
										)}
									</button>
								);
							})}
							{loadingContacts && contacts.length === 0 && (
								<div className="flex items-center justify-center py-6 text-sm text-text-4">
									Loading…
								</div>
							)}
							{!loadingContacts && filtered.length === 0 && (
								<div className="flex items-center justify-center py-6 text-center text-sm text-text-4">
									{q
										? 'No contacts match your search.'
										: 'This client has no contacts yet.'}
								</div>
							)}
						</div>
					</div>

					{hidden.length > 0 && (
						<div className="border-t border-line-1 px-4 py-2.5 text-sm text-text-3">
							{hidden.length === 1
								? '1 selected contact can’t be emailed any more.'
								: `${hidden.length} selected contacts can’t be emailed any more.`}{' '}
							<button
								className="text-text-1 underline underline-offset-2"
								onClick={() =>
									setDraft((prev) => prev.filter((id) => !hidden.includes(id)))
								}
							>
								Remove
							</button>
							{hidden.some((id) => refused[id]) && (
								<div className="mt-1 text-status-danger">
									{hidden
										.map((id) => refused[id])
										.filter(Boolean)
										.join(' · ')}
								</div>
							)}
						</div>
					)}
				</DialogBody>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button onClick={() => onSave(draft)} disabled={saving}>
						{saving
							? 'Saving…'
							: draft.length === 0
								? 'Save (everyone)'
								: `Save (${draft.length})`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
